import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, getDocs, addDoc, query, orderBy, where, serverTimestamp } from 'firebase/firestore';

const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeReview, setActiveReview] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    rating: 5,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Fetch approved reviews from Firestore
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        // Query only approved reviews, ordered by timestamp (newest first)
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('approved', '==', true),
          orderBy('timestamp', 'desc')
        );
        const reviewsSnapshot = await getDocs(reviewsQuery);
        
        // Convert to array of data
        const reviewsData = reviewsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setReviews(reviewsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError('Failed to load reviews. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rating' ? parseInt(value) : value
    }));
  };

  // Handle star rating click
  const handleRatingClick = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim() || !formData.message.trim()) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      setSubmitting(true);
      
      // Add review to Firestore with approved: false
      await addDoc(collection(db, 'reviews'), {
        name: formData.name.trim(),
        rating: formData.rating,
        message: formData.message.trim(),
        approved: false,
        timestamp: serverTimestamp()
      });

      // Reset form and show success message
      setFormData({
        name: '',
        rating: 5,
        message: ''
      });
      setSubmitSuccess(true);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
      
    } catch (err) {
      console.error('Error submitting review:', err);
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const slideIn = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const renderRating = (rating) => {
    return Array(5).fill(0).map((_, index) => (
      <StarIcon 
        key={index} 
        className={`h-5 w-5 ${index < rating ? 'text-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };

  // Color classes for review cards
  const reviewColors = ['bg-blue-50', 'bg-purple-50', 'bg-emerald-50', 'bg-amber-50', 'bg-red-50', 'bg-indigo-50'];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900 to-secondary-900">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url('/grid.svg')`,
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[600px] h-[600px] rounded-full bg-secondary-500/20 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[600px] h-[600px] rounded-full bg-primary-500/20 blur-3xl"></div>
        
        <div className="relative container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="space-y-6"
            >
              <motion.h1 
                className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight"
                variants={fadeIn}
              >
                Real Stories from <span className="text-primary-400">Real Builders</span>
              </motion.h1>
              <motion.p 
                className="text-xl text-gray-200"
                variants={fadeIn}
              >
                Discover how decking professionals across the country are revolutionizing their businesses with NailTheCost
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-8 max-w-7xl mx-auto">
              {error}
            </div>
          )}
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="max-w-7xl mx-auto"
            >
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
                <p className="text-xl text-gray-600">
                  {reviews.length > 0 
                    ? `See how businesses like yours are growing with our platform`
                    : 'Be the first to share your experience!'}
                </p>
              </div>

              {reviews.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No reviews yet. Check back soon!</p>
                </div>
              ) : (
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                  {/* Featured Review */}
                  {reviews.length > 0 && (
                    <motion.div
                      variants={slideIn}
                      className="relative"
                    >
                      <div className="absolute -inset-4 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 rounded-3xl blur-lg"></div>
                      <div className="relative bg-white rounded-2xl shadow-xl p-8">
                        <div className="mb-6">
                          <h3 className="font-semibold text-xl text-gray-900 mb-2">{reviews[activeReview]?.name || 'Anonymous'}</h3>
                        </div>
                        <div className="flex mb-6">
                          {renderRating(reviews[activeReview]?.rating || 5)}
                        </div>
                        <p className="text-lg text-gray-700 italic">"{reviews[activeReview]?.message || ''}"</p>
                      </div>
                    </motion.div>
                  )}

                  {/* Review Grid */}
                  {reviews.length > 1 && (
                    <div className="grid grid-cols-2 gap-6">
                      {reviews.slice(1, 5).map((review, index) => (
                        <motion.div
                          key={review.id}
                          variants={fadeIn}
                          className={`${reviewColors[index % reviewColors.length]} rounded-xl p-6 cursor-pointer transform transition-all duration-300 hover:scale-105 ${activeReview === index + 1 ? 'ring-2 ring-primary-500' : ''}`}
                          onClick={() => setActiveReview(index + 1)}
                        >
                          <div className="mb-4">
                            <h3 className="font-medium text-gray-900 mb-1">{review.name || 'Anonymous'}</h3>
                          </div>
                          <div className="flex mb-2">
                            {renderRating(review.rating)}
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-3">{review.message}</p>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Additional Reviews List */}
              {reviews.length > 5 && (
                <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {reviews.slice(5).map((review, index) => (
                    <motion.div
                      key={review.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className={`${reviewColors[index % reviewColors.length]} rounded-xl p-6`}
                    >
                      <div className="mb-4">
                        <h3 className="font-medium text-gray-900 mb-1">{review.name || 'Anonymous'}</h3>
                      </div>
                      <div className="flex mb-2">
                        {renderRating(review.rating)}
                      </div>
                      <p className="text-sm text-gray-700">{review.message}</p>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* Review Submission Form */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Share Your Experience</h2>
                <p className="text-xl text-gray-600">
                  We'd love to hear how Nail the Cost has helped your business
                </p>
              </div>

              {submitSuccess && (
                <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                  Thank you! Your review is pending approval.
                </div>
              )}

              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
                <div className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                      placeholder="Enter your company name"
                    />
                  </div>

                  {/* Rating Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rating <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center space-x-2">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => handleRatingClick(rating)}
                          className="focus:outline-none transition-transform hover:scale-110"
                        >
                          <StarIcon
                            className={`h-8 w-8 ${
                              rating <= formData.rating
                                ? 'text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-gray-600">
                        {formData.rating} out of 5
                      </span>
                    </div>
                  </div>

                  {/* Message Field */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Review <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all resize-none"
                      placeholder="Share your experience with Nail the Cost..."
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full px-8 py-4 bg-primary-600 text-white font-medium rounded-xl shadow-lg hover:bg-primary-700 transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {submitting ? 'Submitting...' : 'Submit Review'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="space-y-8"
            >
              <motion.h2 
                variants={fadeIn}
                className="text-4xl font-bold text-gray-900 mb-6"
              >
                Ready to Transform Your Decking Business?
              </motion.h2>
              <motion.p 
                variants={fadeIn}
                className="text-xl text-gray-600 mb-8"
              >
                Join hundreds of satisfied customers who have optimized their operations with Nail the Cost
              </motion.p>
              <motion.div 
                variants={fadeIn}
                className="flex flex-col sm:flex-row justify-center gap-4"
              >
                <Link 
                  to="/demo" 
                  className="px-8 py-4 bg-primary-600 text-white font-medium rounded-xl shadow-lg hover:bg-primary-700 transition-all duration-300 transform hover:-translate-y-1"
                >
                  Request a Demo
                </Link>
                <Link 
                  to="/pricing" 
                  className="px-8 py-4 bg-gray-900 text-white font-medium rounded-xl shadow-lg hover:bg-gray-800 transition-all duration-300 transform hover:-translate-y-1"
                >
                  View Pricing
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials; 
